import { Switch, Tooltip } from "antd";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import ListView from "../../components/ListView/ListView";
import { getAllFeatureFlags, toggleFeatureFlags } from "../../store/actions/featureFlag.actions";
import permissionsUtil from "../../utils/permissions.util";
import { getFeatureFlags } from "../../utils/utils";
import "./FeatureFlag.scss";

const AI_AGENT_PILOT_MARKETS = "AI Agent-Pilot Markets";
const USE_READY_FOR_MARKETING_FLAG_ID = "USE_READY_FOR_MARKETING_FLAG";
const DRY_RUN_IMPORT_FLAG_ID = "DRY_RUN_IMPORT";
const isDryRunImportChildFlag = record =>
  typeof record?.id === "string" && record.id.startsWith(`${DRY_RUN_IMPORT_FLAG_ID}_`);

const FeatureFlag = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { list, loading, mutationLoading } = useSelector(store => store.featureFlag);
  const [filterInfo, setFilterInfo] = useState(null);
  const [sortInfo, setSortInfo] = useState(null);

  const canEditFeatureFlag = permissionsUtil.checkAuth("feature_flag_edit");
  const { USE_READY_FOR_MARKETING_FLAG } = getFeatureFlags();
  const isReadyForMarketingFlagEnabled = USE_READY_FOR_MARKETING_FLAG?.status || false;
  const isDryRunImportEnabled = list?.some(flag => flag.id === DRY_RUN_IMPORT_FLAG_ID && flag.status === true) || false;

  const isToggleDisabled = record =>
    !canEditFeatureFlag ||
    (record.id === AI_AGENT_PILOT_MARKETS && !record.status && !isReadyForMarketingFlagEnabled) ||
    (isDryRunImportChildFlag(record) && !isDryRunImportEnabled);

  const fetchFeatureFlag = async () => {
    try {
      await dispatch(getAllFeatureFlags());
    } catch (ex) {
      console.log("feature flag getAll error", ex);
    }
  };

  const toggleFeatureFlag = async record => {
    try {
      if (isToggleDisabled(record)) {
        return;
      }

      if (record.id === USE_READY_FOR_MARKETING_FLAG_ID && record.status) {
        const aiAgentPilotMarketsFlag = list?.find(featureFlag => featureFlag.id === AI_AGENT_PILOT_MARKETS);

        if (aiAgentPilotMarketsFlag?.status) {
          await dispatch(toggleFeatureFlags({ ...aiAgentPilotMarketsFlag, status: false }));
        }
      }

      record = { ...record, status: !record.status };
      await dispatch(toggleFeatureFlags(record));
      await fetchFeatureFlag();
    } catch (ex) {
      console.log("feature flag update error:", ex);
    }
  };

  const columns = [
    {
      title: t("Table.FeatureFlag.FieldLabel"),
      dataIndex: "id",
      key: "id",
      width: 300,
      sorter: (a, b) => b.id.localeCompare(a.id),
      columnFilter: true,
      defaultSortOrder: "asc",
    },
    {
      title: t("Table.FeatureFlag.Description"),
      dataIndex: "description",
      key: "description",
    },
    {
      title: t("Table.Action"),
      key: "action",
      width: 300,
      render: (text, record) => (
        <div className="action-buttons">
          <Tooltip placement="bottom" title={t("Buttons.Toggle")}>
            <Switch
              disabled={isToggleDisabled(record)}
              onChange={() => toggleFeatureFlag(record)}
              checked={record.status}
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <div className="feature-flag-container">
      <ListView
        setFilterInfo={setFilterInfo}
        setSortInfo={setSortInfo}
        rowKey="id"
        dataSource={list}
        getList={fetchFeatureFlag}
        columns={columns}
        loading={loading || mutationLoading}
        scroll={{ y: "calc(100vh - 500px)" }}
      />
    </div>
  );
};

export default FeatureFlag;
